import { useState, useCallback, useEffect } from "react";
import { API_URLS } from "../utils/apiConstants";
import { tutorApiService } from "../services/tutorApi";

/**
 * useTutorSharing Hook
 *
 * URL 공유 기능 관리
 * - 공유 URL 생성 (group_id 또는 storage_id)
 * - QR 코드 표시
 * - 클립보드 복사
 */
export const useSharingFeature = () => {
  const [uuid, setUuid] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  /**
   * 공유 URL 생성
   *
   * @param target 'group_id' | 'storage_id'
   * @param id 그룹 또는 보관함 ID
   */
  const createShareUrl = useCallback(
    async (
      target: "group_id" | "storage_id",
      id: number
    ): Promise<string | null> => {
      setLoading(true);
      setError(null);
      setCopySuccess(false);

      try {
        const reqData = {
          [target]: id,
        };

        const resData = await tutorApiService.touchAPI(
          "POST",
          API_URLS.CREATE_URL,
          reqData
        );

        if (resData && resData.uuid) {
          setUuid(resData.uuid);
          return resData.uuid;
        }

        return null;
      } catch (err) {
        console.error("createShareUrl 에러:", err);
        setError(err instanceof Error ? err : new Error("URL 생성 실패"));
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * 클립보드에 URL 복사
   */
  const copyToClipboard = useCallback(async (url: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);

      // 2초 후 성공 상태 초기화
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);

      return true;
    } catch (err) {
      console.error("copyToClipboard 에러:", err);
      setError(err instanceof Error ? err : new Error("클립보드 복사 실패"));
      return false;
    }
  }, []);

  /**
   * 공유 URL 전체 경로 반환
   */
  const getShareUrl = useCallback(
    (uuidParam?: string): string | null => {
      const targetUuid = uuidParam || uuid;
      if (!targetUuid) return null;
      return `${API_URLS.REFERER_URL}/share/${targetUuid}`;
    },
    [uuid]
  );

  /**
   * UUID 자동 복사 (uuid 생성 직후)
   */
  useEffect(() => {
    if (uuid) {
      const shareUrl = getShareUrl(uuid);
      if (shareUrl) {
        copyToClipboard(shareUrl);
      }
    }
  }, [uuid, getShareUrl, copyToClipboard]);

  /**
   * 상태 초기화
   */
  const reset = useCallback(() => {
    setUuid(null);
    setError(null);
    setCopySuccess(false);
  }, []);

  return {
    uuid,
    loading,
    error,
    copySuccess,
    createShareUrl,
    copyToClipboard,
    getShareUrl,
    reset,
  };
};
